/**
 * @file ProfileDataContent.js
 * @module src/js/components/ProfileDataContent
 * @description
 * **A reusable component for displaying and editing user profile data in the EDALab frontend.**
 * - Accepts profile data and updated profile data as props.
 * - Displays input fields for editable data when `showInputs` is true.
 * - Shows static profile information when `showInputs` is false.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import ChooseAFile from "./ChooseAFile";
import { InputInvalidMessage, TextWithBr } from "./UtilityComponents";

export default function ProfileDataContent({
    showInputs,
    profileData,
    updatedProfileData,
    setUpdatedProfileData,
    updatedImgFile,
    setUpdatedImgFile,
}) {
    return (
        <>
            {showInputs ? (
                <>
                    <b>Name</b>&nbsp;
                    <InputInvalidMessage condition={updatedProfileData.name === ''} />
                    <input type="text" placeholder="張耀文"
                        value={updatedProfileData.name || ''} onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, name: e.target.value })}
                        style={{ marginTop: '5px', marginBottom: '5px' }} />
                </>
            ) : (
                <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                    <b>Name:</b> <TextWithBr text={profileData.name} />
                </div>
            )}          
            {profileData.group === 'student' ? (
                <>
                    {showInputs ? (
                        <>
                            <b>Institute</b>
                            <select value={updatedProfileData.institute || ''}
                                onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, institute: e.target.value })}
                                style={{ marginTop: '5px', marginBottom: '5px' }}
                            >
                                <option value="電子所">電子所</option>
                                <option value="電機所">電機所</option>
                                <option value="資工所">資工所</option>
                                <option value="重點科技學院">重點科技學院</option>
                                <option value="電資學院資料科學學程">電資學院資料科學學程</option>
                            </select>
                            <b>Current Degree</b>
                            <select value={updatedProfileData.level || ''}
                                onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, level: parseInt(e.target.value) })}
                                style={{ marginTop: '5px', marginBottom: '5px' }}
                            >
                                <option value={1}>M.S.</option>
                                <option value={2}>Ph.D.</option>
                                <option value={4}>Postdoc</option>
                            </select>
                        </>
                    ) : (
                        <>
                            <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                                <b>Institute:</b> {profileData.institute} (student)
                            </div>
                            <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                                <b>Current Degree:</b> {profileData.level === 1 ? 'M.S.' :
                                    profileData.level === 2 ? 'Ph.D.' :
                                    profileData.level === 4 ? 'Postdoc' : ''}
                            </div>
                        </>
                    )}
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Current Grade:</b> {profileData.year}
                    </div>
                </>
            ) : profileData.group === 'grad' ? (
                <>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Degree:</b> {profileData.final_grad} (graduate)
                    </div>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Graduation Year:</b> {profileData.grad_year}
                    </div>
                </>
            ) : <></>}
            <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                <b>E-mail:</b> {profileData.mail}
            </div>
            {showInputs ? (
                <>
                    <b>Cellphone Number</b>&nbsp;
                    <InputInvalidMessage condition={updatedProfileData.phone === ''} />
                    <input type="text" placeholder="0912345678"
                        value={updatedProfileData.phone || ''} onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, phone: e.target.value })}
                        style={{ marginTop: '5px', marginBottom: '5px' }} />
                    <b>Address</b>&nbsp;
                    <InputInvalidMessage condition={updatedProfileData.address === ''} />
                    <input type="text" placeholder="106319臺北市羅斯福路四段一號"
                        value={updatedProfileData.address || ''} onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, address: e.target.value })}
                        style={{ marginTop: '5px', marginBottom: '5px' }} />
                    <b>Home Phone Number</b>&nbsp;
                    <InputInvalidMessage condition={updatedProfileData.home_phone === ''} />
                    <input type="text" placeholder="0233663366"
                        value={updatedProfileData.home_phone || ''} onChange={(e) => setUpdatedProfileData({ ...updatedProfileData, home_phone: e.target.value })}
                        style={{ marginTop: '5px', marginBottom: '5px' }} />
                    <b>Profile Image</b>&nbsp;
                    <span style={{ fontSize: '0.8em' }}>
                        (Optional. Replace the current one.)
                    </span>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <ChooseAFile
                            accept={'.jpg, .jpeg, .png'} // need a space after comma
                            files={updatedImgFile}
                            setFiles={setUpdatedImgFile}
                            alwaysDark={true}
                        />
                    </div> 
                </>
            ) : (
                <>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Cellphone Number:</b> {profileData.phone}
                    </div>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Address:</b> {profileData.address}
                    </div>
                    <div style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <b>Home Phone Number:</b> {profileData.home_phone}
                    </div>
                </>
            )}
        </>
    );
}